import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  Banknote,
  Box,
  CalendarClock,
  CircleDollarSign,
  Mail,
  MapPin,
  PackageCheck,
  Phone,
  Printer,
  ReceiptText,
  Save,
  ShoppingBag,
  Truck,
  UserRound,
  WalletCards,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import { FilterPanel, SortableHeader } from "../../components/data-controls";
import { LiaDateRangeField } from "../../components/date-picker";
import {
  ActionMenu,
  Badge,
  Button,
  Card,
  Confirm,
  CopyValue,
  DataTableShell,
  EmptyState,
  ErrorState,
  FormErrorSummary,
  Input,
  Loading,
  PageHeader,
  Pagination,
  SearchBox,
  Select,
  SummaryGrid,
} from "../../components/ui";
import { adminApi, assetUrl } from "../../lib/api";
import { orderStatusSchema, type OrderStatusFormValues } from "../../lib/form-schemas";
import { dateTime, money, number } from "../../lib/format";
import { labelOf } from "../../lib/labels";
import { PERMISSIONS, hasPermission } from "../../lib/permissions";
import { useAuthStore } from "../../store/auth-store";
import type { OrderDetail, OrderListItem, OrderStatus } from "../../types/api";

const statuses: OrderStatus[] = [
  "PendingPayment",
  "PaymentReview",
  "Paid",
  "Processing",
  "Shipped",
  "Delivered",
  "Cancelled",
  "Refunded",
];
const paymentStatuses = ["Pending", "Succeeded", "Failed", "Refunded", "Cancelled"] as const;
const toStartIso = (value: string) => value ? new Date(`${value}T00:00:00`).toISOString() : undefined;
const toEndIso = (value: string) => value ? new Date(`${value}T23:59:59.999`).toISOString() : undefined;

function paymentTone(value: string): "success" | "warning" | "danger" | "neutral" {
  if (value === "Succeeded") return "success";
  if (["Failed", "Cancelled", "Refunded"].includes(value)) return "danger";
  if (value === "Pending") return "warning";
  return "neutral";
}

function CustomerCell({ order }: { order: OrderListItem }) {
  const content = <span className="customer-cell__text customer-cell__text--plain"><strong>{order.customerName || "مشتری مهمان"}</strong><small dir="ltr">{order.customerPhone || "—"}</small></span>;
  return order.userId
    ? <Link className="customer-cell customer-cell--plain customer-cell--link" to={`/users/${order.userId}`}>{content}</Link>
    : <div className="customer-cell customer-cell--plain">{content}</div>;
}

export function OrderListPage() {
  const [status, setStatus] = useState<OrderStatus | "">("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [shippingMethodId, setShippingMethodId] = useState("");
  const [search, setSearch] = useState("");
  const [minTotal, setMinTotal] = useState("");
  const [maxTotal, setMaxTotal] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const summaryQuery = useQuery({ queryKey: ["orders", "summary"], queryFn: adminApi.orders.summary });
  const shippingQuery = useQuery({ queryKey: ["shipping-methods"], queryFn: adminApi.shipping.list });
  const query = useQuery({
    queryKey: ["orders", status, paymentStatus, shippingMethodId, search, minTotal, maxTotal, from, to, sort, page, pageSize],
    queryFn: () => adminApi.orders.list({
      status,
      paymentStatus: paymentStatus || undefined,
      shippingMethodId: shippingMethodId || undefined,
      search: search.trim() || undefined,
      minTotal: minTotal ? Number(minTotal) : undefined,
      maxTotal: maxTotal ? Number(maxTotal) : undefined,
      from: toStartIso(from),
      to: toEndIso(to),
      sort,
      page,
      pageSize,
    }),
  });

  const activeFilters = [search, status, paymentStatus, shippingMethodId, minTotal, maxTotal, from, to].filter(Boolean).length;
  const changeSort = (value: string) => { setSort(value); setPage(1); };
  const clearFilters = () => {
    setSearch(""); setStatus(""); setPaymentStatus(""); setShippingMethodId(""); setMinTotal(""); setMaxTotal(""); setFrom(""); setTo(""); setSort("newest"); setPage(1);
  };

  return (
    <>
      <PageHeader title="مدیریت سفارش‌ها" description="مشتری، اقلام، پرداخت و روند آماده‌سازی هر سفارش را در یک نمای فروش حرفه‌ای پیگیری کنید." />

      {summaryQuery.data ? <SummaryGrid items={[
        { label: "کل سفارش‌ها", value: number(summaryQuery.data.total), icon: <ShoppingBag />, tone: "teal" },
        { label: "در انتظار پرداخت", value: number(summaryQuery.data.pendingPayment), icon: <WalletCards />, tone: "gold" },
        { label: "در حال پردازش", value: number(summaryQuery.data.processing), icon: <PackageCheck />, tone: "neutral" },
        { label: "ارسال‌شده", value: number(summaryQuery.data.shipped), icon: <Truck />, tone: "teal" },
        { label: "مبلغ سفارش‌های جاری", value: money(summaryQuery.data.currentAmount), icon: <CircleDollarSign />, tone: "green" },
        { label: "مبلغ پرداخت موفق", value: money(summaryQuery.data.paidAmount), icon: <Banknote />, tone: "green" },
      ]} /> : null}

      <FilterPanel search={<SearchBox value={search} onChange={(value) => { setSearch(value); setPage(1); }} placeholder="شماره سفارش، مشتری، موبایل، ایمیل یا کد رهگیری..." />} activeCount={activeFilters} totalLabel={`${number(query.data?.totalCount ?? 0)} سفارش`} onClear={clearFilters}>
        <Select label="وضعیت سفارش" value={status} onChange={(event) => { setStatus(event.target.value as OrderStatus | ""); setPage(1); }}><option value="">همه وضعیت‌ها</option>{statuses.map((value) => <option value={value} key={value}>{labelOf(value)}</option>)}</Select>
        <Select label="وضعیت پرداخت" value={paymentStatus} onChange={(event) => { setPaymentStatus(event.target.value); setPage(1); }}><option value="">همه پرداخت‌ها</option>{paymentStatuses.map((value) => <option value={value} key={value}>{labelOf(value)}</option>)}</Select>
        <Select label="روش ارسال" value={shippingMethodId} onChange={(event) => { setShippingMethodId(event.target.value); setPage(1); }}><option value="">همه روش‌ها</option>{(shippingQuery.data ?? []).map((method) => <option value={method.id} key={method.id}>{method.name}</option>)}</Select>
        <Input label="حداقل مبلغ" type="number" min="0" value={minTotal} onChange={(event) => { setMinTotal(event.target.value); setPage(1); }} />
        <Input label="حداکثر مبلغ" type="number" min="0" value={maxTotal} onChange={(event) => { setMaxTotal(event.target.value); setPage(1); }} />
        <LiaDateRangeField
          className="filter-date-range"
          label="بازه ثبت سفارش"
          start={from}
          end={to}
          onChange={(range) => { setFrom(range.start); setTo(range.end); setPage(1); }}
          hint="تاریخ‌ها به‌صورت شمسی نمایش داده می‌شوند و برای API با تاریخ واقعی میلادی ارسال می‌شوند."
        />
        <Select label="مرتب‌سازی" value={sort} onChange={(event) => changeSort(event.target.value)}>
          <option value="newest">جدیدترین</option><option value="oldest">قدیمی‌ترین</option><option value="amount-desc">بیشترین مبلغ</option><option value="amount-asc">کمترین مبلغ</option><option value="customer-asc">نام مشتری — صعودی</option><option value="customer-desc">نام مشتری — نزولی</option><option value="status-asc">وضعیت سفارش</option>
        </Select>
      </FilterPanel>

      {query.isLoading ? <Loading /> : query.error ? <ErrorState error={query.error} retry={() => void query.refetch()} /> : !query.data?.items.length ? <EmptyState title="سفارشی پیدا نشد" description="فیلترها را تغییر دهید یا عبارت جست‌وجوی دیگری وارد کنید." /> : (
        <DataTableShell pagination={<Pagination page={query.data.page} totalPages={query.data.totalPages} totalCount={query.data.totalCount} pageSize={pageSize} onChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />}>
          <table className="product-table management-table order-table order-table--rich">
            <thead><tr>
              <th>ردیف</th>
              <th>سفارش</th>
              <SortableHeader label="مشتری" value={sort} ascValue="customer-asc" descValue="customer-desc" onChange={changeSort} />
              <SortableHeader label="وضعیت" value={sort} ascValue="status-asc" onChange={changeSort} />
              <th>پرداخت / ارسال</th>
              <SortableHeader label="مبلغ نهایی" value={sort} ascValue="amount-asc" descValue="amount-desc" defaultDirection="desc" onChange={changeSort} />
              <SortableHeader label="تاریخ" value={sort} ascValue="oldest" descValue="newest" defaultDirection="desc" onChange={changeSort} />
              <th>عملیات</th>
            </tr></thead>
            <tbody>{query.data.items.map((order, index) => <tr key={order.id}>
              <td data-label="ردیف"><span className="row-number">{number((query.data.page - 1) * pageSize + index + 1)}</span></td>
              <td data-label="سفارش"><div className="order-copy-stack"><CopyValue label="شماره سفارش" value={order.orderNumber} /><CopyValue label="کد رهگیری" value={order.trackingCode} /></div></td>
              <td data-label="مشتری"><CustomerCell order={order} /></td>
              <td data-label="وضعیت"><Badge value={labelOf(order.status)} /></td>
              <td data-label="پرداخت / ارسال"><div className="cell-badges"><Badge value={labelOf(order.paymentStatus)} tone={paymentTone(order.paymentStatus)} /><small>{order.shippingMethod || "روش ارسال نامشخص"}</small></div></td>
              <td data-label="مبلغ نهایی"><strong>{money(order.grandTotal)}</strong></td>
              <td data-label="تاریخ"><span className="table-date">{dateTime(order.createdAt)}</span></td>
              <td data-label="عملیات"><ActionMenu><Link to={`/orders/${order.id}`}>مشاهده سفارش</Link>{order.userId ? <Link to={`/users/${order.userId}`}>پروفایل مشتری</Link> : null}</ActionMenu></td>
            </tr>)}</tbody>
          </table>
        </DataTableShell>
      )}
    </>
  );
}

function PrintableInvoice({ order }: { order: OrderDetail }) {
  return (
    <section className={`invoice-print${order.items.length > 8 ? " invoice-print--dense" : ""}${order.items.length > 14 ? " invoice-print--ultra-dense" : ""}`} aria-label="فاکتور چاپی سفارش">
      <header className="invoice-print__header">
        <div className="invoice-print__brand"><img src="/lia-logo-app.png" alt="عطر لیا" /><div><h1>عطر لیا</h1><p>فروشگاه تخصصی عطر و ادکلن</p><small>فاکتور فروش سفارش اینترنتی</small></div></div>
        <div className="invoice-print__meta"><span>شماره فاکتور</span><strong dir="ltr">{order.orderNumber}</strong><span>تاریخ صدور</span><strong>{dateTime(order.createdAt)}</strong></div>
      </header>
      <div className="invoice-print__title"><span>فاکتور فروش</span><small>نسخه قابل چاپ سفارش</small></div><div className="invoice-print__divider" />
      <div className="invoice-print__parties">
        <section><h2>اطلاعات خریدار</h2><p><strong>{order.customerName}</strong></p><p>تلفن: <span dir="ltr">{order.customerPhone}</span></p>{order.customerEmail ? <p>ایمیل: <span dir="ltr">{order.customerEmail}</span></p> : null}</section>
        <section><h2>نشانی تحویل</h2><p>{order.address.province}، {order.address.city}، {order.address.fullAddress}</p><p>کد پستی: {order.address.postalCode}</p><p>گیرنده: {order.address.recipientName} · {order.address.phoneNumber}</p></section>
      </div>
      <table className="invoice-print__table"><thead><tr><th>ردیف</th><th>شرح کالا</th><th>SKU</th><th>تعداد</th><th>قیمت واحد</th><th>مبلغ</th></tr></thead><tbody>{order.items.map((item, index) => <tr key={item.variantId}><td>{number(index + 1)}</td><td><strong>{item.name}</strong><small>{item.brand} · {item.volume}</small></td><td dir="ltr">{item.sku}</td><td>{number(item.quantity)}</td><td>{money(item.unitPrice)}</td><td>{money(item.lineTotal)}</td></tr>)}</tbody></table>
      <div className="invoice-print__footer-grid">
        <div><h3>اطلاعات ارسال</h3><p>روش ارسال: {order.shippingMethod}</p><p>کد رهگیری: {order.trackingCode || "هنوز ثبت نشده"}</p><p>وضعیت سفارش: {labelOf(order.status)}</p></div>
        <dl><div><dt>جمع کالاها</dt><dd>{money(order.subtotal)}</dd></div><div><dt>تخفیف</dt><dd>− {money(order.discountAmount)}</dd></div><div><dt>هزینه ارسال</dt><dd>{money(order.shippingAmount)}</dd></div><div className="invoice-print__grand"><dt>مبلغ قابل پرداخت</dt><dd>{money(order.grandTotal)}</dd></div></dl>
      </div>
      <footer><div><strong>عطر لیا</strong><p>این فاکتور بر اساس اطلاعات قطعی سفارش در سامانه فروش صادر شده است.</p></div><div className="invoice-print__stamp">مهر / امضا</div></footer>
    </section>
  );
}

export function OrderDetailPage() {
  const user = useAuthStore((state) => state.user);
  const canManageOrder = hasPermission(user, PERMISSIONS.ordersManage);
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [dangerValues, setDangerValues] = useState<OrderStatusFormValues | null>(null);
  const query = useQuery({ queryKey: ["order", id], queryFn: () => adminApi.orders.detail(id!) });
  const form = useForm<OrderStatusFormValues>({ resolver: zodResolver(orderStatusSchema), mode: "onBlur", defaultValues: { status: "PendingPayment", trackingCode: "" } });

  useEffect(() => { if (query.data) form.reset({ status: query.data.status, trackingCode: query.data.trackingCode ?? "" }); }, [form, query.data]);

  const mutation = useMutation({
    mutationFn: (values: OrderStatusFormValues) => adminApi.orders.updateStatus(id!, values.status, values.trackingCode?.trim() || undefined),
    onSuccess: async (updated) => {
      setDangerValues(null); form.reset({ status: updated.status, trackingCode: updated.trackingCode ?? "" }); await query.refetch();
      await Promise.all([queryClient.invalidateQueries({ queryKey: ["orders"] }), queryClient.invalidateQueries({ queryKey: ["orders", "summary"] })]);
    },
  });
  const submitStatus = (values: OrderStatusFormValues) => ["Cancelled", "Refunded"].includes(values.status) ? setDangerValues(values) : mutation.mutate(values);

  if (query.isLoading) return <Loading />;
  if (query.error || !query.data) return <ErrorState error={query.error || new Error("سفارش پیدا نشد")} retry={() => void query.refetch()} />;
  const order = query.data;

  return (
    <>
      <PageHeader title={`سفارش ${order.orderNumber}`} description={`ثبت‌شده در ${dateTime(order.createdAt)} · ${order.customerName}`} actions={<><Button type="button" variant="secondary" onClick={() => window.print()}><Printer size={17} /> چاپ فاکتور</Button><Link className="button button--ghost" to="/orders"><ArrowRight size={17} /> بازگشت</Link></>} />
      <div className="order-summary-strip"><div><span>وضعیت سفارش</span><Badge value={labelOf(order.status)} /></div><div><span>وضعیت پرداخت</span><Badge value={labelOf(order.paymentStatus)} tone={paymentTone(order.paymentStatus)} /></div><div><span>مبلغ نهایی</span><strong>{money(order.grandTotal)}</strong></div><div><span>روش ارسال</span><strong>{order.shippingMethod}</strong></div></div>

      <div className="editor-grid order-detail-grid">
        <div className="editor-main">
          <Card title="اطلاعات مشتری" description="مشخصات ثبت‌شده هنگام نهایی‌سازی سفارش.">
            <div className="order-customer-grid">
              <div className="order-info-item"><UserRound /><div><span>نام مشتری</span>{order.userId ? <Link className="text-link" to={`/users/${order.userId}`}>{order.customerName}</Link> : <strong>{order.customerName}</strong>}</div></div>
              <div className="order-info-item"><Phone /><div><span>شماره تماس</span><a dir="ltr" href={`tel:${order.customerPhone}`}>{order.customerPhone}</a></div></div>
              <div className="order-info-item"><Mail /><div><span>ایمیل</span>{order.customerEmail ? <a dir="ltr" href={`mailto:${order.customerEmail}`}>{order.customerEmail}</a> : <strong>ثبت نشده</strong>}</div></div>
              <div className="order-info-item"><CalendarClock /><div><span>زمان ثبت</span><strong>{dateTime(order.createdAt)}</strong></div></div>
            </div>
            {order.customerNote ? <div className="order-customer-note"><span>یادداشت مشتری</span><p>{order.customerNote}</p></div> : null}
          </Card>

          <Card title={`اقلام سفارش · ${number(order.items.reduce((sum, item) => sum + item.quantity, 0))} قلم`} description="هر کالا مستقیماً به صفحه جزئیات محصول در پنل متصل است.">
            <div className="order-items">{order.items.map((item) => <Link className="order-item order-item--link" to={`/products/${item.productId}/details`} key={item.variantId}>
              <div className="product-thumb product-thumb--large">{item.image ? <img src={assetUrl(item.image)} alt={item.name} /> : <Box />}</div>
              <div><strong>{item.name}</strong><span>{item.brand} · {item.volume}</span><small dir="ltr">SKU: {item.sku}</small></div>
              <div><span>{number(item.quantity)} × {money(item.unitPrice)}</span><strong>{money(item.lineTotal)}</strong></div>
            </Link>)}</div>
          </Card>

          <div className="two-column-cards">
            <Card title="خلاصه مالی"><dl className="totals"><div><dt>جمع کالاها</dt><dd>{money(order.subtotal)}</dd></div><div><dt>تخفیف</dt><dd className="success-text">− {money(order.discountAmount)}</dd></div><div><dt>هزینه ارسال</dt><dd>{money(order.shippingAmount)}</dd></div><div className="totals__final"><dt>مبلغ نهایی</dt><dd>{money(order.grandTotal)}</dd></div></dl></Card>
            <Card title="نشانی تحویل"><div className="address-card"><MapPin /><div><strong>{order.address.recipientName}</strong><p>{order.address.province}، {order.address.city}، {order.address.fullAddress}</p><span>کد پستی: {order.address.postalCode}</span><span><Phone size={15} /> {order.address.phoneNumber}</span></div></div></Card>
          </div>
        </div>

        <aside className="editor-side order-detail-side">
          {canManageOrder ? <section className="order-status-control"><div className="order-status-control__head"><strong>مدیریت وضعیت سفارش</strong><p>تغییر وضعیت باید مطابق مرحله واقعی سفارش انجام شود.</p></div><div className="order-status-control__body"><form className="stack" noValidate onSubmit={form.handleSubmit(submitStatus)}><FormErrorSummary errors={[form.formState.errors.status?.message, form.formState.errors.trackingCode?.message]} /><Select label="وضعیت جدید" error={form.formState.errors.status?.message} {...form.register("status")}>{statuses.map((value) => <option value={value} key={value}>{labelOf(value)}</option>)}</Select><Input label="کد رهگیری" placeholder="پس از ارسال وارد شود" hint="کد قابل رهگیری شرکت حمل‌ونقل را پس از تحویل مرسوله ثبت کنید." error={form.formState.errors.trackingCode?.message} {...form.register("trackingCode")} />{mutation.error ? <small className="field__error">{mutation.error.message}</small> : null}<Button type="submit" busy={mutation.isPending} disabled={!form.formState.isDirty}><Save size={17} /> ثبت تغییرات</Button></form></div></section> : <Card title="دسترسی فقط‌خواندنی" description="نقش فعلی امکان تغییر وضعیت یا کد رهگیری سفارش را ندارد."><p className="muted">اطلاعات سفارش و فاکتور برای مشاهده در دسترس است.</p></Card>}
          <Card title="زمان‌بندی سفارش" description="رویدادهای اصلی سفارش بر اساس زمان ثبت‌شده در سیستم."><ul className="timeline"><li className="is-done"><ReceiptText /><div><strong>ثبت سفارش</strong><span>{dateTime(order.createdAt)}</span></div></li><li className={order.paidAt ? "is-done" : ""}><WalletCards /><div><strong>پرداخت</strong><span>{dateTime(order.paidAt)}</span></div></li><li className={order.shippedAt ? "is-done" : ""}><Truck /><div><strong>ارسال</strong><span>{dateTime(order.shippedAt)}</span></div></li><li className={order.deliveredAt ? "is-done" : ""}><PackageCheck /><div><strong>تحویل</strong><span>{dateTime(order.deliveredAt)}</span></div></li></ul></Card>
        </aside>
      </div>

      <PrintableInvoice order={order} />
      <Confirm open={Boolean(dangerValues)} title={dangerValues?.status === "Refunded" ? "ثبت بازپرداخت سفارش" : "لغو سفارش"} description={dangerValues?.status === "Refunded" ? "با ثبت وضعیت بازپرداخت، وضعیت پرداخت سفارش نیز بازپرداخت‌شده خواهد شد. پیش از ادامه از انجام عملیات مالی مطمئن شوید." : "لغو سفارش یک تغییر مهم در فرآیند فروش است. پیش از تأیید، وضعیت پرداخت و هماهنگی با مشتری را بررسی کنید."} confirmLabel={dangerValues?.status === "Refunded" ? "ثبت بازپرداخت" : "تأیید لغو"} busy={mutation.isPending} onClose={() => setDangerValues(null)} onConfirm={() => { if (dangerValues) mutation.mutate(dangerValues); }} />
    </>
  );
}
